import { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useCookieConsent } from '@/hooks/use-cookie-consent';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface CookieCategory {
  id: 'necessary' | 'functional' | 'analytics' | 'marketing';
  label: string;
  description: string;
  detail: string;
  disabled?: boolean;
}

const COOKIE_CATEGORIES: CookieCategory[] = [
  {
    id: 'necessary',
    label: 'Necesare',
    description:
      'Cookie-uri esentiale pentru functionarea platformei: autentificare, securitate, preferinte cookie.',
    detail:
      'Aceste cookie-uri sunt strict necesare pentru functionarea site-ului si nu pot fi dezactivate. Includ cookie-uri pentru sesiune, autentificare, token CSRF si preferintele tale privind cookie-urile.',
    disabled: true,
  },
  {
    id: 'functional',
    label: 'Functionale',
    description:
      'Permit platformei sa retina preferintele tale de afisare si functionalitatile personalizate.',
    detail:
      'Aceste cookie-uri permit platformei sa ofere functionalitati imbunatatite si personalizare, cum ar fi limba preferata, tema de afisare sau completarea automata a formularelor.',
  },
  {
    id: 'analytics',
    label: 'Analitice',
    description:
      'Ne ajuta sa intelegem cum este utilizata platforma pentru a o imbunatati. (Google Analytics)',
    detail:
      'Folosim Google Analytics pentru a colecta informatii anonime despre modul in care vizitatorii interactioneaza cu platforma. Aceste date ne ajuta sa identificam paginile populare, sa detectam probleme si sa imbunatatim experienta utilizatorilor.',
  },
  {
    id: 'marketing',
    label: 'Marketing',
    description:
      'Utilizate pentru a afisa reclame relevante pe alte platforme. (Facebook Pixel, Google Ads)',
    detail:
      'Aceste cookie-uri sunt setate de partenerii nostri de publicitate (Facebook, Google) pentru a crea un profil al intereselor tale si a-ti afisa reclame relevante pe alte site-uri. Nu stocheaza informatii personale direct, dar identifica browserul si dispozitivul tau.',
  },
];

export function CookieSettingsModal() {
  const { isSettingsOpen, closeSettings, acceptAll, savePreferences, consent } = useCookieConsent();

  const [functional, setFunctional] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  // Sync local state with stored consent when modal opens
  useEffect(() => {
    if (isSettingsOpen && consent) {
      setFunctional(consent.functional);
      setAnalytics(consent.analytics);
      setMarketing(consent.marketing);
    }
  }, [isSettingsOpen, consent]);

  function toggleExpanded(id: string) {
    setExpandedCategory((prev) => (prev === id ? null : id));
  }

  function getToggleValue(id: CookieCategory['id']): boolean {
    switch (id) {
      case 'necessary':
        return true;
      case 'functional':
        return functional;
      case 'analytics':
        return analytics;
      case 'marketing':
        return marketing;
    }
  }

  function setToggleValue(id: CookieCategory['id'], value: boolean) {
    switch (id) {
      case 'functional':
        setFunctional(value);
        break;
      case 'analytics':
        setAnalytics(value);
        break;
      case 'marketing':
        setMarketing(value);
        break;
    }
  }

  function handleSave() {
    savePreferences({ functional, analytics, marketing });
  }

  return (
    <Dialog open={isSettingsOpen} onOpenChange={(open) => !open && closeSettings()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Preferinte cookie-uri</DialogTitle>
          <DialogDescription>
            Alege ce tipuri de cookie-uri doresti sa accepti. Cookie-urile necesare sunt
            intotdeauna active deoarece sunt esentiale pentru functionarea platformei.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {COOKIE_CATEGORIES.map((category) => (
            <div
              key={category.id}
              className="rounded-xl border border-stone-200 bg-stone-50/50 p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <button
                    type="button"
                    onClick={() => toggleExpanded(category.id)}
                    className="flex items-center gap-1.5 text-sm font-medium text-stone-800 hover:text-stone-950"
                  >
                    {category.label}
                    <ChevronDown
                      className={cn(
                        'h-3.5 w-3.5 text-stone-400 transition-transform duration-200',
                        expandedCategory === category.id && 'rotate-180',
                      )}
                    />
                  </button>
                  <p className="mt-1 text-xs leading-relaxed text-stone-500">
                    {category.description}
                  </p>
                </div>

                <Switch
                  checked={getToggleValue(category.id)}
                  onCheckedChange={(value) => setToggleValue(category.id, value)}
                  disabled={category.disabled}
                  aria-label={`Toggle ${category.label}`}
                />
              </div>

              {expandedCategory === category.id && (
                <div className="mt-3 border-t border-stone-200 pt-3">
                  <p className="text-xs leading-relaxed text-stone-500">{category.detail}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={acceptAll}>
            Accepta toate
          </Button>
          <Button onClick={handleSave}>Salveaza preferintele</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
